import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listGarmin, getGarminCategories } from "../lib/api.js";
import type { GarminListItem, GarminCategory } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, HashIcon, ChevronRightIcon } from "../lib/icons.js";

/**
 * Garmin katalog — distributorský XML feed + scraped data z garmin.com.
 *
 * Karty zobrazují obrázek, název, cenu s DPH, status skladem.
 * Klik na kartu → detail stránka `/garmin/:code`.
 */
export function GarminPage(): JSX.Element {
  const [categories, setCategories] = useState<GarminCategory[] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProductType, setSelectedProductType] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [items, setItems] = useState<GarminListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const LIMIT = 48;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset offset + subcategory když změníme hlavní kategorii
  useEffect(() => {
    setOffset(0);
    setSelectedProductType(null);
  }, [selectedCategory]);

  useEffect(() => {
    setOffset(0);
  }, [selectedProductType, debouncedSearch, inStockOnly]);

  useEffect(() => {
    getGarminCategories()
      .then((d) => setCategories(d.categories))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listGarmin({
      category: selectedCategory ?? undefined,
      productType: selectedProductType ?? undefined,
      search: debouncedSearch || undefined,
      inStock: inStockOnly,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        if (cancelled) return;
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => !cancelled && setItems([]))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selectedCategory, selectedProductType, debouncedSearch, inStockOnly, offset]);

  // Subkategorie pro aktuálně vybranou kategorii (nebo celkově pokud "Vše")
  const activeSubcategories = useMemo(() => {
    if (!categories) return [];
    if (selectedCategory === null) {
      // Agreguj napříč všemi
      const map = new Map<string, { type: string; label: string; count: number }>();
      for (const c of categories) {
        for (const s of c.subcategories) {
          const existing = map.get(s.type);
          if (existing) existing.count += s.count;
          else map.set(s.type, { ...s });
        }
      }
      return Array.from(map.values()).sort((a, b) => b.count - a.count);
    }
    return categories.find((c) => c.name === selectedCategory)?.subcategories ?? [];
  }, [categories, selectedCategory]);

  if (!categories) return <LoadingBlock label="Načítám kategorie…" />;

  const totalActive = categories.reduce((s, c) => s + c.count, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">
          Garmin
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          GPS navigace, hodinky, kamery a příslušenství Garmin. Data z
          distributorského feedu, bohaté popisy postupně doplňované z garmin.com.
        </p>
      </header>

      {/* Category tabs (úroveň 1) */}
      <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
        <CategoryTab
          active={selectedCategory === null}
          onClick={() => setSelectedCategory(null)}
          name="Vše"
          count={totalActive}
        />
        {categories.map((c) => (
          <CategoryTab
            key={c.name}
            active={selectedCategory === c.name}
            onClick={() => setSelectedCategory(c.name)}
            name={c.name}
            count={c.count}
          />
        ))}
      </div>

      {/* Subcategory chips (úroveň 2) */}
      {activeSubcategories.length > 0 && (
        <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1">
          <SubcategoryChip
            active={selectedProductType === null}
            onClick={() => setSelectedProductType(null)}
            label="Všechny typy"
            count={activeSubcategories.reduce((s, x) => s + x.count, 0)}
          />
          {activeSubcategories.map((s) => (
            <SubcategoryChip
              key={s.type}
              active={selectedProductType === s.type}
              onClick={() => setSelectedProductType(s.type)}
              label={s.label}
              count={s.count}
            />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat SKU, název, EAN…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-80"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(e) => setInStockOnly(e.target.checked)}
          />
          Jen skladem
        </label>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {/* Grid */}
      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné výsledky"
          description="Upravte filtr nebo hledání."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <GarminCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z{" "}
            {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function CategoryTab({
  active,
  onClick,
  name,
  count,
}: {
  active: boolean;
  onClick: () => void;
  name: string;
  count: number;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
        active
          ? "bg-ktm-orange text-white border-ktm-orange"
          : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
      }`}
    >
      <span className="font-medium">{name}</span>
      <span className={`ml-2 text-xs ${active ? "text-white/80" : "text-zinc-400"}`}>
        {count.toLocaleString("cs-CZ")}
      </span>
    </button>
  );
}

function SubcategoryChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap border transition-colors ${
        active
          ? "bg-zinc-900 text-white border-zinc-900"
          : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 hover:text-zinc-900"
      }`}
    >
      <span>{label}</span>
      <span className={`ml-1.5 ${active ? "text-white/70" : "text-zinc-400"}`}>
        {count.toLocaleString("cs-CZ")}
      </span>
    </button>
  );
}

function GarminCard({ item }: { item: GarminListItem }): JSX.Element {
  const priceLabel =
    item.priceMocVat != null
      ? item.priceMocVat.toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) + " Kč"
      : null;
  const hasRichData = item.scrapeStatus === "ok";

  return (
    <Link
      to={`/garmin/${encodeURIComponent(item.code)}`}
      className="group bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:border-ktm-orange hover:shadow-md transition-all"
    >
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        {item.imgUrl ? (
          <img
            src={item.imgUrl}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300">
            <PackageIcon size={40} />
          </div>
        )}
        <div className="absolute bottom-2 left-2">
          <Badge tone={item.inStock ? "green" : "neutral"}>
            {item.inStock ? "Skladem" : "Není skladem"}
          </Badge>
        </div>
        {hasRichData && (
          <div className="absolute top-2 right-2">
            <Badge tone="blue">+ detail</Badge>
          </div>
        )}
      </div>

      <div className="p-3">
        <div className="text-xs text-zinc-400 font-mono">{item.code}</div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div>
            {priceLabel ? (
              <div className="text-sm font-semibold text-zinc-900">{priceLabel}</div>
            ) : (
              <span className="text-xs text-zinc-400">Bez ceny</span>
            )}
            {item.priceVocNoVat != null && priceLabel && (
              <div className="text-[11px] text-zinc-500">
                nákup {item.priceVocNoVat.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč bez DPH
              </div>
            )}
          </div>
          <ChevronRightIcon size={14} className="text-zinc-300 group-hover:text-ktm-orange transition-colors" />
        </div>
      </div>
    </Link>
  );
}
